import { AlertTriangle, ExternalLink, HeartPulse, LifeBuoy } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import type { CustomerDetail } from "@busright/data-contract";
import { RAG } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { CustomerHealthChip } from "@/components/CustomerHealthChip";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { useCustomer } from "@/hooks/queries";
import { formatInt, formatScore, formatUsd } from "@/lib/format";

// Customer detail — the 360 for one Linear customer: identity + ARR, joined device health (only
// when the customer matched a warehouse district), support load with deep links back to Linear, and
// a data-derived risk line. Empty states are explicit for unmatched / ticket-free customers.

// One labeled metric in a header / card strip.
const Metric = ({
  label,
  value,
  valueColor,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly valueColor?: string;
}): ReactNode => (
  <div className="metric-chip">
    <span className="mc-label">{label}</span>
    <span
      className="mc-value br-tabular-nums"
      style={valueColor ? { color: valueColor } : undefined}
    >
      {value}
    </span>
  </div>
);

// Synthesize a single honest risk line from the joined data. ARR/tickets are always present; device
// health only when matched. Kept strictly data-derived — no editorializing beyond the numbers.
const riskNarrative = (detail: CustomerDetail): string => {
  const { summary } = detail;
  const parts: string[] = [];
  if (summary.revenueUsd !== null) {
    parts.push(`${formatUsd(summary.revenueUsd)} ARR account`);
  } else {
    parts.push("Account with no ARR on file");
  }
  if (summary.health) {
    const atRisk = summary.health.riskCounts.warn + summary.health.riskCounts.critical;
    parts.push(
      `${formatInt(summary.health.deviceCount)} device(s) tracked, ${formatInt(atRisk)} at risk`,
    );
  } else {
    parts.push("not matched to a warehouse district (no device health)");
  }
  parts.push(`${formatInt(summary.tickets.total)} open ticket(s)`);
  return `${parts.join(" · ")}.`;
};

export const CustomerDetailPage = (): ReactNode => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error } = useCustomer(id ?? "");
  const meta = data?.meta;
  const detail = data?.data;

  if (isError) {
    return (
      <>
        <PageHeader title="Customer detail" meta={meta} />
        <ChartCard title="Customer detail">
          <EmptyState
            title="Couldn't load customer"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <PageHeader title="Customer detail" description={id ? `Customer ${id}` : undefined} />
        <ChartCard title="Customer detail" loading={isLoading}>
          <EmptyState title="No customer" message="This customer id returned no detail." />
        </ChartCard>
      </>
    );
  }

  const { summary } = detail;
  const ticketStates = Object.entries(summary.tickets.byState);

  return (
    <>
      <PageHeader title={summary.name} description={riskNarrative(detail)} meta={meta} />

      <div style={{ marginBottom: 16 }}>
        <ChartCard
          title="Account"
          subtitle="Identity, owner, tier and ARR from Linear."
          source="linear.customers"
          action={
            <a
              href={summary.url}
              target="_blank"
              rel="noreferrer"
              className="card-source"
              style={{ display: "inline-flex", gap: 5, alignItems: "center" }}
            >
              Open in Linear <ExternalLink size={13} aria-hidden />
            </a>
          }
        >
          <div className="metric-strip">
            <Metric label="Status" value={summary.status ?? "—"} />
            <Metric
              label="ARR (proxy)"
              value={summary.revenueUsd === null ? "—" : formatUsd(summary.revenueUsd)}
            />
            <Metric label="State" value={summary.state ?? "—"} />
            <Metric label="Owner" value={summary.owner ?? "—"} />
            <Metric label="Tier" value={summary.tier ?? "—"} />
            <Metric label="Matched district" value={summary.districtId ? "matched" : "unmatched"} />
          </div>
          {summary.domains.length > 0 ? (
            <div
              className="mono"
              style={{ marginTop: 10, fontSize: 12, color: "var(--br-text-muted)" }}
            >
              {summary.domains.join(", ")}
            </div>
          ) : null}
        </ChartCard>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <ChartCard
          title="Device health"
          subtitle="Joined from the matched warehouse district (rpt_hw_device_latest)."
          source="rpt_hw_device_latest"
        >
          {summary.health ? (
            <div className="metric-strip">
              <Metric label="Devices" value={formatInt(summary.health.deviceCount)} />
              <Metric
                label="Avg health"
                value={formatScore(summary.health.avgCompositeHealthScore)}
              />
              <Metric
                label="OK"
                value={formatInt(summary.health.riskCounts.ok)}
                valueColor={RAG.OK}
              />
              <Metric
                label="WARN"
                value={formatInt(summary.health.riskCounts.warn)}
                valueColor={RAG.WARN}
              />
              <Metric
                label="CRIT"
                value={formatInt(summary.health.riskCounts.critical)}
                valueColor={RAG.CRITICAL}
              />
            </div>
          ) : (
            <EmptyState
              title="No device health"
              message="This customer did not fuzzy-match a warehouse district, so no fleet is joined."
              icon={HeartPulse}
            />
          )}
        </ChartCard>

        <ChartCard
          title="Support"
          subtitle="Linear issues linked to this customer's needs. Workflow state is unavailable in bulk, so it reads 'unknown'."
          source="linear.issues"
        >
          {summary.tickets.total > 0 ? (
            <div>
              <div className="metric-strip">
                <Metric label="Open total" value={formatInt(summary.tickets.total)} />
                {ticketStates.map(([state, count]) => (
                  <Metric key={state} label={state} value={formatInt(count)} />
                ))}
              </div>
              <div style={{ marginTop: 4 }}>
                {detail.recentTickets.length > 0 ? (
                  detail.recentTickets.map((t) => (
                    <a
                      key={t.url}
                      href={t.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 0",
                        color: "var(--br-text-primary)",
                        borderTop: "1px solid var(--br-border)",
                      }}
                    >
                      <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        <span>{t.title}</span>
                        <span
                          className="mono"
                          style={{ fontSize: 11, color: "var(--br-text-muted)" }}
                        >
                          {t.state}
                        </span>
                      </span>
                      <ExternalLink
                        size={13}
                        aria-hidden
                        style={{ color: "var(--br-text-muted)" }}
                      />
                    </a>
                  ))
                ) : (
                  <div
                    style={{ marginTop: 8, fontSize: 12, color: "var(--br-text-muted)" }}
                    className="mono"
                  >
                    Counts present, but no individual ticket links in this payload.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <EmptyState
              title="No open tickets"
              message="No Linear issues are linked to this customer."
              icon={LifeBuoy}
            />
          )}
        </ChartCard>
      </div>

      {summary.health ? (
        <div style={{ marginBottom: 16 }}>
          <ChartCard title="Device fleet risk" source="rpt_hw_device_latest">
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <CustomerHealthChip riskCounts={summary.health.riskCounts} />
              <span style={{ fontSize: 12, color: "var(--br-text-muted)" }}>
                OK / WARN / CRITICAL devices in the matched district.
              </span>
            </div>
          </ChartCard>
        </div>
      ) : null}

      <div>
        <Link to="/customers" className="card-source">
          ← Back to all customers
        </Link>
      </div>
    </>
  );
};
